import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { organizationsApi } from '@/api/organizations.api';
import { AppShell } from '@/components/layout/AppShell';
import { formatDateTime } from '@/lib/format';

const PLAN_PRICE: Record<string, string> = {
  free: '0 AZN / ay',
  business: '49 AZN / ay',
  enterprise: 'contact_sales',
};

export function BillingPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['billing'],
    queryFn: () => organizationsApi.billing(),
  });

  if (isLoading || !data) {
    return (
      <AppShell>
        <h1 className="page-title">{t('settings_billing.title')}</h1>
        <div className="card text-center py-12 text-muted">{t('common.loading')}</div>
      </AppShell>
    );
  }

  const planLabel = t(`settings_billing.plan_${data.plan}`);
  const priceRaw = PLAN_PRICE[data.plan] ?? '';
  const price = priceRaw === 'contact_sales' ? t('settings_billing.contact_sales') : priceRaw;

  return (
    <AppShell>
      <div className="mb-6">
        <h1 className="page-title">{t('settings_billing.title')}</h1>
        <p className="page-subtitle">{t('settings_billing.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <div className="card">
          <div className="text-[11px] uppercase font-semibold text-muted tracking-wider">
            {t('settings_billing.current_plan')}
          </div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{planLabel}</div>
          <div className="text-[12px] text-muted mt-0.5">{price}</div>
          {data.trialEndsAt && (
            <div className="text-[12px] text-warning mt-3">
              {t('settings_billing.trial_ends', { date: formatDateTime(data.trialEndsAt) })}
            </div>
          )}
        </div>

        <div className="card">
          <div className="text-[11px] uppercase font-semibold text-muted tracking-wider">
            {t('settings_billing.billing_period')}
          </div>
          <div className="text-[13px] text-gray-900 mt-2">
            <strong>{t('common.from')}:</strong> {formatDateTime(data.periodStart)}
          </div>
          <div className="text-[13px] text-gray-900">
            <strong>{t('common.to')}:</strong> {formatDateTime(data.periodEnd)}
          </div>
        </div>

        <div className="card bg-primary text-white border-primary flex flex-col justify-between">
          <div>
            <div className="text-[14px] font-semibold mb-1">{t('settings_billing.need_more')}</div>
            <p className="text-[12px] text-white/80 m-0">
              {t('settings_billing.need_more_text')}
            </p>
          </div>
          <button
            disabled
            className="btn mt-3"
            style={{ background: '#fff', color: '#0F2A47', border: 0, opacity: 0.7, cursor: 'not-allowed' }}
            title={t('settings_billing.upgrade_disabled_hint')}
          >
            {t('settings_billing.upgrade_coming_soon')}
          </button>
        </div>
      </div>

      <div className="card !p-0 mb-5">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="text-base font-semibold text-gray-900 m-0">{t('settings_billing.usage')}</h3>
        </div>
        <div className="p-4 space-y-4">
          <UsageBar
            label={t('settings_billing.envelopes_month')}
            used={data.envelopes.used}
            limit={data.envelopes.limit}
            unit=""
          />
          <UsageBar
            label={t('settings_billing.active_users')}
            used={data.users.used}
            limit={data.users.limit}
            unit=""
          />
          <div className="flex justify-between text-[13px] text-gray-700">
            <span>{t('settings_billing.bulk_rows_per_dispatch')}</span>
            <strong>{data.bulkSend.limit > 0 ? data.bulkSend.limit : t('settings_billing.unlimited')}</strong>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function UsageBar({
  label,
  used,
  limit,
  unit,
}: {
  label: string;
  used: number;
  limit: number;
  unit: string;
}) {
  const { t } = useTranslation();
  const unlimited = limit <= 0;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const overage = !unlimited && used >= limit;

  return (
    <div>
      <div className="flex justify-between text-[12px] mb-1.5">
        <span className="text-gray-700">{label}</span>
        <span className={overage ? 'text-danger font-semibold' : 'text-gray-900'}>
          <strong>{used.toLocaleString()}</strong> {unit}
          {unlimited ? ` (${t('settings_billing.unlimited')})` : ` / ${limit.toLocaleString()}${unit ? ` ${unit}` : ''}`}
        </span>
      </div>
      {!unlimited && (
        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: `${pct}%`,
              background: overage ? '#EF4444' : pct > 80 ? '#F59E0B' : '#0F2A47',
            }}
          />
        </div>
      )}
      {overage && (
        <div className="text-[11px] text-danger mt-1">
          {t('settings_billing.overage_warning')}
        </div>
      )}
    </div>
  );
}
